import { isDefined } from "@clipboard-health/util-ts";

import {
  LatestWorkerMovement,
  TripStatus,
} from "../containers/facilityDashboard/ShiftCalendar/dayView/workerETA/types";
import { WORKER_ETA_DISTANCE_TOLERANCE } from "../containers/facilityDashboard/ShiftCalendar/dayView/facilityShiftCard/constants";

export function determineIsWorkerAtHome(trip: {
  latestWorkerMovement: LatestWorkerMovement | undefined;
  initialEtaDistance: number | undefined;
  currentEtaDistance: number | undefined;
}): boolean {
  // Without both distances (fact-only trip with no provider ETA, ALCH-6955) "at home" is unknowable.
  if (!isDefined(trip.initialEtaDistance) || !isDefined(trip.currentEtaDistance)) {
    return false;
  }

  return (
    trip.latestWorkerMovement === LatestWorkerMovement.STATIONARY &&
    Math.abs(trip.initialEtaDistance - trip.currentEtaDistance) <= WORKER_ETA_DISTANCE_TOLERANCE
  );
}

export interface TripLike {
  status?: TripStatus;
  latestWorkerMovement?: LatestWorkerMovement;
  currentEtaDistance?: number;
  eta?: { distanceInM?: number };
  stopTime?: string | Date | null;
}

export interface ShiftLike {
  hasWorkerClockedIn?: boolean;
  clockInOut?: { start?: Date | string; clockIn?: Date };
  attendance?: { arrivedFacilityGeofenceAt?: Date | string };
  arrivedFacilityGeofenceAt?: Date | string;
}

/**
 * Sanity check to account for backend merging TRIP_COMPLETED and TRIP_ARRIVED into only TRIP_ARRIVED.
 * If a Trip's status was actually meant to be TRIP_COMPLETED, it means tracking expired, not that worker arrived.
 * See ALCH-5275 for context.
 *
 * @param trip - Trip data (ITripResponseType, ShiftBoardTrip, or similar)
 * @param shift - Shift data (FacilityShiftWithDocument, ShiftBoardShift, IMapTableViewDataSource, or similar)
 * @returns true if we have confirmation the worker actually arrived, false otherwise
 */
export function hasActuallyArrived(trip: TripLike | undefined, shift: ShiftLike): boolean {
  if (!trip || trip.status !== TripStatus.TRIP_ARRIVED) {
    return false;
  }

  if (shift.hasWorkerClockedIn ?? shift.clockInOut?.start ?? shift.clockInOut?.clockIn) {
    return true;
  }

  if (shift.attendance?.arrivedFacilityGeofenceAt ?? shift.arrivedFacilityGeofenceAt) {
    return true;
  }

  // Fallback: stationary within 50m with valid stopTime (if stopTime property exists)
  if ("stopTime" in trip && !trip.stopTime) {
    return false;
  }

  const distance = trip.currentEtaDistance ?? trip.eta?.distanceInM ?? Infinity;
  return trip.latestWorkerMovement === LatestWorkerMovement.STATIONARY && distance <= 50;
}
